import { useState } from "react";
import { ReleaseFlags, ReleaseLink } from "./product";
import { Avatar, ago, api, can, CONTENT_STATUS, Drawer, Empty, fmtDate, go, Loading, Pill, plural, Sample, setQuery, StatusPill, TASK_STATUS, useLoad, useMe, useRoute } from "./lib";

export function TaskRow({ t, onOpen }: { t: any; onOpen: (id: string) => void }) {
  return (
    <div className="item clickable" onClick={() => onOpen(t.id)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen(t.id)}>
      <div className="grow">
        <div className="row" style={{ gap: 8 }}>
          <span className="title">{t.title}</span>
          <Sample on={t.is_sample} />
        </div>
        <div className="row small muted" style={{ gap: 8, marginTop: 2 }}>
          {t.owner_name && <span>{t.owner_name}</span>}
          {t.links?.filter((l: any) => l.label).map((l: any) => <span key={l.to_id}>· {l.label}</span>)}
          {t.subtask_count > 0 && <span>· {plural(t.subtask_count, "subtask")}</span>}
          {t.recurrence && <span>· Repeats {t.recurrence}</span>}
        </div>
      </div>
      {t.is_blocked && <Pill tone="bad">{t.status === "blocked" ? "Blocked" : "Waiting on another task"}</Pill>}
      {t.priority === "urgent" || t.priority === "high" ? <Pill tone="warn">{t.priority === "urgent" ? "Urgent" : "High"}</Pill> : null}
      {t.due_on && <Pill tone={t.is_overdue ? "bad" : ""}>{t.is_overdue ? "Overdue · " : "Due "}{fmtDate(t.due_on)}</Pill>}
      {!t.is_blocked && t.status !== "todo" && <StatusPill s={t.status} map={TASK_STATUS} />}
    </div>
  );
}

function Section({ title, sub, items, onOpen, empty }: { title: string; sub?: string; items: any[]; onOpen: (id: string) => void; empty: string }) {
  return (
    <section className="card">
      <h2>
        {title} <span className="muted small">{items.length || ""}</span>
      </h2>
      {sub && <p className="sub">{sub}</p>}
      {items.length ? <div className="list">{items.map((t) => <TaskRow key={t.id} t={t} onOpen={onOpen} />)}</div> : <Empty>{empty}</Empty>}
    </section>
  );
}

export function MyWork() {
  const { me } = useMe();
  const { data, error, reload } = useLoad<any>("/api/my-work");
  const route = useRoute();
  const [adding, setAdding] = useState(false);
  const open = (id: string) => setQuery({ task: id });
  if (!data) return <div className="page"><Loading error={error} /></div>;
  const needsMe = data.my_content.filter((c: any) => c.status === "changes_requested" || c.status === "publish_failed");
  return (
    <div className="page">
      <div className="row between">
        <p className="muted" style={{ margin: 0 }}>
          {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "America/Chicago" })}
        </p>
        <button className="btn primary" onClick={() => setAdding(true)}>New task</button>
      </div>

      {(data.approvals_for_me.length > 0 || needsMe.length > 0) && (
        <div className="grid">
          {data.approvals_for_me.length > 0 && (
            <section className="card">
              <h2>Waiting on your review</h2>
              <p className="sub">Content someone asked you to approve.</p>
              <div className="list">
                {data.approvals_for_me.map((a: any) => (
                  <div key={a.id} className="item clickable" onClick={() => go("/marketing", { content: a.record_id })}>
                    <div className="grow">
                      <div className="title">{a.title}</div>
                      <div className="small muted">v{a.version} from {a.requested_by_name} · {ago(a.created_at)}</div>
                    </div>
                    <Pill tone="info">Review</Pill>
                  </div>
                ))}
              </div>
            </section>
          )}
          {needsMe.length > 0 && (
            <section className="card">
              <h2>Your content needs you</h2>
              <p className="sub">Feedback to address or a post that didn't go out.</p>
              <div className="list">
                {needsMe.map((c: any) => (
                  <div key={c.id} className="item clickable" onClick={() => go("/marketing", { content: c.id })}>
                    <div className="grow title">{c.title}</div>
                    <StatusPill s={c.status} map={CONTENT_STATUS} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ReleaseFlags flags={data.release_flags} onResolved={reload} />

      <div className="grid">
        <Section title="Overdue" items={data.overdue} onOpen={open} empty="Nothing overdue." />
        <Section title="Due in the next 7 days" items={data.due_soon} onOpen={open} empty="Nothing due this week." />
      </div>
      <div className="grid">
        <Section title="Blocked" sub="Marked blocked, or waiting on another task." items={data.blocked} onOpen={open} empty="Nothing blocked." />
        <section className="card">
          <h2>Recent comments on your work</h2>
          {data.recent_comments.length ? (
            <div className="list">
              {data.recent_comments.map((c: any) => (
                <div
                  key={c.id}
                  className="item clickable"
                  onClick={() => (c.record_type === "content" ? go("/marketing", { content: c.record_id }) : setQuery({ task: c.record_id }))}
                >
                  <div className="grow">
                    <div className="small"><strong>{c.author_name}</strong> <span className="muted">{ago(c.created_at)}{c.version ? ` · on v${c.version}` : ""}</span></div>
                    <div className="small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.body}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Empty>No new comments.</Empty>
          )}
        </section>
      </div>
      <Section title="Later" sub="No date, or due after next week." items={data.later} onOpen={open} empty="Nothing else on your list." />
      {can(me, "content.view") && data.my_content.length > 0 && (
        <section className="card">
          <h2>Your content in progress</h2>
          <div className="list">
            {data.my_content.map((c: any) => (
              <div key={c.id} className="item clickable" onClick={() => go("/marketing", { content: c.id })}>
                <div className="grow title">{c.title}</div>
                <StatusPill s={c.status} map={CONTENT_STATUS} />
              </div>
            ))}
          </div>
        </section>
      )}

      {route.q.get("task") && <TaskDrawer id={route.q.get("task")!} onClose={() => setQuery({ task: null })} onChange={reload} />}
      {adding && <NewTask onClose={() => setAdding(false)} onCreated={(id) => (setAdding(false), reload(), open(id))} />}
    </div>
  );
}

export function Tasks() {
  const { me } = useMe();
  const route = useRoute();
  const q = route.q;
  const scope = q.get("scope") || "mine";
  const owner = scope === "mine" ? "" : q.get("owner") || "";
  const dept = scope === "everyone" ? q.get("department") || "" : "";
  const status = q.get("status") || "open";
  const due = q.get("due") || "";
  const qs = new URLSearchParams({ ...(scope !== "everyone" && { scope }), ...(owner && { owner }), ...(dept && { department: dept }), ...(status && { status }), ...(due && { due }) }).toString();
  const { data, error, reload } = useLoad<any[]>("/api/tasks?" + qs);
  const users = useLoad<any[]>("/api/users").data || [];
  const [adding, setAdding] = useState(false);
  const depts = [...new Set(users.map((u) => u.department).filter(Boolean))];
  return (
    <div className="page">
      <div className="row between">
        <div className="row">
          <div className="tabs" role="tablist" aria-label="Whose tasks">
            {([["mine", "Mine"], ...(me.department ? [["team", "My team"]] : []), ["everyone", "Everyone"]] as [string, string][]).map(([k, label]) => (
              <button key={k} role="tab" aria-selected={scope === k} className={scope === k ? "on" : ""} onClick={() => setQuery({ scope: k === "mine" ? null : k, owner: null, department: null })}>{label}</button>
            ))}
          </div>
          {scope !== "mine" && (
            <select className="input" style={{ width: "auto" }} value={owner} onChange={(e) => setQuery({ owner: e.target.value || null })} aria-label="Owner">
              <option value="">{scope === "team" ? "Anyone on my team" : "Anyone"}</option>
              {users.filter((u) => scope !== "team" || u.department === me.department).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {scope === "everyone" && (
            <select className="input" style={{ width: "auto" }} value={dept} onChange={(e) => setQuery({ department: e.target.value || null })} aria-label="Department">
              <option value="">All departments</option>
              {depts.map((d) => <option key={d} value={d}>{d[0]!.toUpperCase() + d.slice(1)}</option>)}
            </select>
          )}
          <select className="input" style={{ width: "auto" }} value={status} onChange={(e) => setQuery({ status: e.target.value })} aria-label="Status">
            <option value="open">Open</option>
            {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select className="input" style={{ width: "auto" }} value={due} onChange={(e) => setQuery({ due: e.target.value })} aria-label="Deadline">
            <option value="">Any deadline</option>
            <option value="overdue">Overdue</option>
            <option value="week">Due in 7 days</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
        <div className="row">
          {can(me, "export.data") && <a className="btn" href="/api/export/tasks.csv">Export CSV</a>}
          <button className="btn primary" onClick={() => setAdding(true)}>New task</button>
        </div>
      </div>
      {scope === "everyone" && !can(me, "tasks.view_all") && <p className="muted small" style={{ margin: 0 }}>You see your own tasks, tasks you made, and your department's tasks.</p>}
      <section className="card" style={{ padding: "8px 16px" }}>
        {!data ? <Loading error={error} /> : data.length ? <div className="list">{data.map((t) => <TaskRow key={t.id} t={t} onOpen={(id) => setQuery({ task: id })} />)}</div> : <Empty>{scope === "mine" && status === "open" && !due ? "Nothing open is yours right now." : "No tasks match these filters."}</Empty>}
      </section>
      {q.get("task") && <TaskDrawer id={q.get("task")!} onClose={() => setQuery({ task: null })} onChange={reload} />}
      {adding && <NewTask onClose={() => setAdding(false)} onCreated={(id) => (setAdding(false), reload(), setQuery({ task: id }))} />}
    </div>
  );
}

export function NewTask({ onClose, onCreated, parentId, link }: { onClose: () => void; onCreated: (id: string) => void; parentId?: string; link?: { type: string; id: string; label: string } }) {
  const { me, toast } = useMe();
  const users = useLoad<any[]>("/api/users").data || [];
  const campaigns = useLoad<any[]>(can(me, "campaigns.view") ? "/api/campaigns" : null).data || [];
  const [f, setF] = useState({ title: "", description: "", owner_id: me.id, due_on: "", priority: "normal", recurrence: "", campaign: "" });
  const set = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Drawer title={parentId ? "New subtask" : "New task"} onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const links = link ? [{ type: link.type, id: link.id }] : f.campaign ? [{ type: "campaign", id: f.campaign }] : [];
            const r = await api("/api/tasks", { method: "POST", body: { ...f, recurrence: f.recurrence || null, parent_id: parentId, links } });
            toast("Task saved");
            onCreated(r.id);
          } catch (e: any) {
            toast(e.message, true);
          }
        }}
      >
        <label className="field">Title<input className="input" value={f.title} onChange={(e) => set("title", e.target.value)} required autoFocus /></label>
        <label className="field">Details<textarea className="input" value={f.description} onChange={(e) => set("description", e.target.value)} /></label>
        <div className="form-2">
          <label className="field">Owner
            <select className="input" value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)}>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </label>
          <label className="field">Due<input className="input" type="date" value={f.due_on} onChange={(e) => set("due_on", e.target.value)} /></label>
          <label className="field">Priority
            <select className="input" value={f.priority} onChange={(e) => set("priority", e.target.value)}>
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
          </label>
          <label className="field">Repeats
            <select className="input" value={f.recurrence} onChange={(e) => set("recurrence", e.target.value)}>
              <option value="">Doesn't repeat</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
            </select>
          </label>
        </div>
        {link ? (
          <p className="small muted" style={{ margin: 0 }}>Linked to {link.label}</p>
        ) : (
          campaigns.length > 0 && (
            <label className="field">Related campaign
              <select className="input" value={f.campaign} onChange={(e) => set("campaign", e.target.value)}>
                <option value="">None</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          )
        )}
        <div className="row"><button className="btn primary">Save task</button><button type="button" className="btn" onClick={onClose}>Cancel</button></div>
      </form>
    </Drawer>
  );
}

export function TaskDrawer({ id, onClose, onChange }: { id: string; onClose: () => void; onChange: () => void }) {
  const { toast } = useMe();
  const { data: t, error, reload } = useLoad<any>(`/api/tasks/${id}`, [id]);
  const users = useLoad<any[]>("/api/users").data || [];
  const allTasks = useLoad<any[]>("/api/tasks?status=open").data || [];
  const [comment, setComment] = useState("");
  const [sub, setSub] = useState(false);
  const patch = async (body: any) => {
    try {
      const r = await api(`/api/tasks/${id}`, { method: "PATCH", body });
      reload();
      onChange();
      if (r.next_recurring_id) toast("Done. The next one is on the list.");
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  if (!t) return <Drawer title="Task" onClose={onClose}><Loading error={error} /></Drawer>;
  const ro = !t.can_edit;
  return (
    <Drawer
      title={t.title}
      onClose={onClose}
      sub={<div className="row"><StatusPill s={t.status} map={TASK_STATUS} />{t.is_overdue && <Pill tone="bad">Overdue</Pill>}<Sample on={t.is_sample} /></div>}
    >
      {ro && <div className="banner info">You can see this task but only its owner, the person who made it, or an admin can change it.</div>}
      <div className="form-2">
        <label className="field">Status
          <select className="input" disabled={ro} value={t.status} onChange={(e) => patch({ status: e.target.value })}>
            {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <label className="field">Owner
          <select className="input" disabled={ro} value={t.owner_id || ""} onChange={(e) => patch({ owner_id: e.target.value })}>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </label>
        <label className="field">Due
          <input className="input" type="date" disabled={ro} defaultValue={t.due_on || ""} onBlur={(e) => e.target.value !== (t.due_on || "") && patch({ due_on: e.target.value || null })} />
        </label>
        <label className="field">Priority
          <select className="input" disabled={ro} value={t.priority} onChange={(e) => patch({ priority: e.target.value })}>
            <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
          </select>
        </label>
      </div>
      {t.status === "blocked" && (
        <label className="field">What's blocking it
          <input className="input" disabled={ro} defaultValue={t.blocked_reason || ""} onBlur={(e) => patch({ blocked_reason: e.target.value })} placeholder="Waiting on…" />
        </label>
      )}
      <label className="field">Details
        <textarea className="input" disabled={ro} defaultValue={t.description} onBlur={(e) => e.target.value !== t.description && patch({ description: e.target.value })} />
      </label>

      <section className="stack">
        <strong className="small">Linked to</strong>
        {t.links.length ? (
          <div className="row">{t.links.map((l: any) => <a key={l.to_id} className="pill" href={l.to_type === "campaign" ? `#/marketing?tab=campaigns&campaign=${l.to_id}` : l.to_type === "organization" ? `#/partnerships?org=${l.to_id}` : l.to_type === "opportunity" ? `#/pr?opp=${l.to_id}` : `#/marketing?content=${l.to_id}`}>{l.label || l.to_type}</a>)}</div>
        ) : (
          <span className="muted small">Nothing yet.</span>
        )}
      </section>

      <ReleaseLink type="task" id={id} canEdit={!ro} />

      <section className="stack">
        <strong className="small">Waits on</strong>
        {t.dependencies.length > 0 && (
          <div className="row">
            {t.dependencies.map((d: any) => <span key={d.id} className={`pill ${d.status === "done" ? "good" : ""}`}>{d.title}</span>)}
          </div>
        )}
        {!ro && (
          <select className="input" value="" onChange={(e) => e.target.value && patch({ depends_on: [...t.dependencies.map((d: any) => d.id), e.target.value] })}>
            <option value="">Add a task this waits on…</option>
            {allTasks.filter((x) => x.id !== t.id && !t.dependencies.some((d: any) => d.id === x.id)).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
          </select>
        )}
      </section>

      <section className="stack">
        <div className="row between"><strong className="small">Subtasks</strong>{!ro && <button className="btn sm" onClick={() => setSub(true)}>Add subtask</button>}</div>
        {t.subtasks.length ? <div className="list">{t.subtasks.map((s: any) => <TaskRow key={s.id} t={s} onOpen={(sid) => setQuery({ task: sid })} />)}</div> : <span className="muted small">None.</span>}
      </section>

      <section className="stack">
        <strong className="small">Comments</strong>
        {t.comments.map((c: any) => (
          <div key={c.id} className="comment"><div className="who row" style={{ gap: 8 }}><Avatar id={c.author_id} name={c.author_name} hasPhoto={c.author_has_photo} size={22} />{c.author_name} <span className="muted tiny">{ago(c.created_at)}</span></div><p>{c.body}</p></div>
        ))}
        <form
          className="row"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            await api("/api/comments", { method: "POST", body: { record_type: "task", record_id: id, body: comment } }).catch((e) => toast(e.message, true));
            setComment("");
            reload();
          }}
        >
          <input className="input" style={{ flex: 1 }} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment" />
          <button className="btn">Post</button>
        </form>
      </section>

      <section className="stack">
        <strong className="small">History</strong>
        <div className="timeline">
          {t.activity.map((a: any) => <div key={a.id}><time>{ago(a.created_at)}</time><span>{a.actor_name || "System"}: {a.action.replace(/^task\./, "").replace(/_/g, " ")}</span></div>)}
        </div>
      </section>
      {sub && <NewTask parentId={id} onClose={() => setSub(false)} onCreated={() => (setSub(false), reload(), onChange())} />}
    </Drawer>
  );
}

// Your work, for the top of Today: only the parts that have something in them.
export function MyWorkSections({ skipApprovals = false }: { skipApprovals?: boolean }) {
  const { me } = useMe();
  const { data, reload } = useLoad<any>("/api/my-work");
  const route = useRoute();
  const open = (id: string) => setQuery({ task: id });
  if (!data || !me) return null;
  const needsMe = data.my_content.filter((c: any) => c.status === "changes_requested" || c.status === "publish_failed");
  const approvals = skipApprovals ? [] : data.approvals_for_me;
  const nothing = !data.overdue.length && !data.due_soon.length && !data.blocked.length && !approvals.length && !needsMe.length && !data.release_flags.length;
  return (
    <>
      <div className="row between">
        <h2 style={{ margin: 0, font: "600 19px var(--font-display)" }}>Your work</h2>
        <a className="btn sm" href="#/tasks">All my tasks</a>
      </div>
      {nothing ? (
        <Empty>Nothing overdue, due this week, blocked or waiting on your review.</Empty>
      ) : (
        <div className="grid">
          {data.overdue.length > 0 && <Section title="Overdue" items={data.overdue} onOpen={open} empty="" />}
          {data.due_soon.length > 0 && <Section title="Due in the next 7 days" items={data.due_soon} onOpen={open} empty="" />}
          {data.blocked.length > 0 && <Section title="Blocked" sub="Marked blocked, or waiting on another task." items={data.blocked} onOpen={open} empty="" />}
          {approvals.length > 0 && (
            <section className="card">
              <h2>Waiting on your review</h2>
              <div className="list">
                {approvals.map((a: any) => (
                  <div key={a.id} className="item clickable" onClick={() => go("/marketing", { content: a.record_id })}>
                    <div className="grow"><div className="title">{a.title}</div><div className="small muted">from {a.requested_by_name} · {ago(a.created_at)}</div></div>
                    <Pill tone="info">Review</Pill>
                  </div>
                ))}
              </div>
            </section>
          )}
          {needsMe.length > 0 && (
            <section className="card">
              <h2>Your content needs you</h2>
              <div className="list">
                {needsMe.map((c: any) => (
                  <div key={c.id} className="item clickable" onClick={() => go("/marketing", { content: c.id })}>
                    <div className="grow title">{c.title}</div>
                    <StatusPill s={c.status} map={CONTENT_STATUS} />
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
      <ReleaseFlags flags={data.release_flags} onResolved={reload} />
      {data.recent_comments.length > 0 && (
        <section className="card">
          <h2>New comments on your work</h2>
          <div className="list">
            {data.recent_comments.slice(0, 5).map((c: any) => (
              <div key={c.id} className="item clickable" onClick={() => (c.record_type === "content" ? go("/marketing", { content: c.record_id }) : setQuery({ task: c.record_id }))}>
                <div className="grow">
                  <div className="small"><strong>{c.author_name}</strong> <span className="muted">{ago(c.created_at)}</span></div>
                  <div className="small" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {route.q.get("task") && <TaskDrawer id={route.q.get("task")!} onClose={() => setQuery({ task: null })} onChange={reload} />}
    </>
  );
}

// What you handed to someone else and is still open, oldest and overdue first.
export function WaitingOnOthers() {
  const { data } = useLoad<any[]>("/api/waiting");
  if (!data) return null;
  return (
    <section className="card stack">
      <div className="row between">
        <h2 style={{ margin: 0 }}>Waiting on others</h2>
        <span className="muted tiny">Things you handed off that aren't finished</span>
      </div>
      {data.length ? (
        <div className="list">
          {data.map((w) => (
            <a key={w.type + w.id} className="item clickable" href={w.href} style={{ color: "inherit", textDecoration: "none" }}>
              <Pill tone={w.overdue ? "warn" : ""}>{w.type}</Pill>
              <div className="grow">
                <div className="title">{w.title}</div>
                <div className="small muted">{w.who ?? "Someone"} · {w.overdue ? `overdue${w.due_on ? `, was due ${w.due_on}` : ""}` : `handed off ${ago(w.since)}`}</div>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <span className="muted small">Nothing outstanding. When you give someone a task, hand off a decision or send content for review, it shows here until it's done.</span>
      )}
    </section>
  );
}
